/** @format */

import { z } from "zod";
import { Brand, BRANDS, validateLanguage } from "./config/brands";
import { hasBalancedMathDelimiters } from "./math";
import {
	getQuestionContentText,
	QUESTION_ASSET_WIDTHS,
	QUESTION_STATUSES,
	REJECTION_REASON_MAX_LENGTH,
	QuestionStatus,
	CORE_TIER_IDS,
	CORE_SERIES_IDS,
	CORE_ASSESSMENT_OBJECTIVE_IDS,
	type QuestionAssetWidth,
} from "./question";

// A Core Taxonomy reference (DR10/DR11): qualification, tier and series season
// are stored as { code, uuid?, title? }. `code` is required and authoritative.
const coreRefSchema = z.object({
	code: z.string().trim().min(1),
	uuid: z.string().trim().uuid().optional(),
	title: z.string().trim().min(1).optional(),
});

const seriesSchema = z.object({
	// Season is a Core reference (DR11); the id scheme uses season.code.
	season: coreRefSchema,
	year: z
		.number({ required_error: "Year is required", invalid_type_error: "Year is required" })
		.int()
		.min(2000, "Year must be after 2000")
		.max(2100),
});

const optionalInt = z.number().int().optional();
const optionalTrimmedString = z.string().trim().min(1).optional();
// Core Taxonomy API identifiers (QB2-309). Stored as optional UUID strings,
// matching the Core API's `uuidSchema`; additive so existing records stay valid.
const optionalUuid = z.string().trim().uuid().optional();

const assessmentObjectivesSchema = z
	.object({
		ao1: optionalInt,
		ao2: optionalInt,
		ao3: optionalInt,
	})
	.optional();

const specificationSchema = z
	.object({
		theme: optionalTrimmedString,
		topic: optionalTrimmedString,
		subTopic: optionalTrimmedString,
		coreLineageId: optionalUuid,
		coreEditionId: optionalUuid,
	})
	.optional();

// Core API reference-data id mappings (QB2-224). Each id is validated against
// the Core API's fixed option set via the readonly-array -> z.enum cast idiom,
// then re-cast to the precise union so the parsed type stays exact. All fields
// optional and additive, so existing records stay valid.
const coreMappingsSchema = z
	.object({
		subjectId: optionalTrimmedString,
		subjectCategoryId: optionalTrimmedString,
		tierId: z.enum(CORE_TIER_IDS as unknown as [string, ...string[]]).optional() as unknown as z.ZodOptional<
			z.ZodEnum<["1", "2", "3"]>
		>,
		seriesId: z.enum(CORE_SERIES_IDS as unknown as [string, ...string[]]).optional() as unknown as z.ZodOptional<
			z.ZodEnum<["1", "2", "3", "4"]>
		>,
		assessmentObjectiveIds: z
			.array(z.enum(CORE_ASSESSMENT_OBJECTIVE_IDS as unknown as [string, ...string[]]))
			.optional() as unknown as z.ZodOptional<z.ZodArray<z.ZodEnum<["1", "2", "3"]>>>,
	})
	.optional();

const metadataSchema = z
	.object({
		questionType: optionalTrimmedString,
		demandMarks: z
			.object({
				low: optionalInt,
				medium: optionalInt,
				high: optionalInt,
			})
			.optional(),
		keyWord: optionalTrimmedString,
		optional: z.boolean().optional(),
		commonWithOtherTier: optionalTrimmedString,
		seededOrDoubleMarked: optionalTrimmedString,
		markingMethod: z.enum(["A", "E"]).optional(),
	})
	.optional();

// Localised (per-language) fields that remain outside the shared content
// blocks: the mark scheme image for each language.
const localisedImageFields = {
	markSchemeImage: z.string().nullable().optional(),
};

/**
 * Normalise the "empty-ish" values a half-filled form produces ("", null, or
 * the `NaN` a number input yields — which serialises to `null` over the wire)
 * to `undefined` so optional fields validate instead of erroring.
 */
const blankToUndefined = (value: unknown) =>
	value === "" || value === null || (typeof value === "number" && Number.isNaN(value)) ? undefined : value;

// Optional display width for an image block, as one of the fixed percentages.
// An unset width is normalised away so existing questions (and a form's empty
// "default" option) keep the renderers' own default sizing.
const assetWidthSchema = z.preprocess(
	blankToUndefined,
	z
		.number({ invalid_type_error: "Image width must be a percentage" })
		.refine((value): value is QuestionAssetWidth => (QUESTION_ASSET_WIDTHS as readonly number[]).includes(value), {
			message: `Image width must be one of ${QUESTION_ASSET_WIDTHS.join(", ")}%`,
		})
		.optional(),
);

// A single ordered body block: either per-language prose or a shared image
// asset. Authors arrange one shared list so a question can interleave text and
// images in any order.
const contentBlockSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("text"),
		en: z.string().trim().max(5000, "Question content should not exceed 5000 characters").optional(),
		cy: z.string().trim().max(5000, "Question content should not exceed 5000 characters").optional(),
	}),
	z.object({
		type: z.literal("asset"),
		en: z.string().trim().min(1).optional(),
		cy: z.string().trim().min(1).optional(),
		enRaster: z.string().trim().min(1).optional(),
		cyRaster: z.string().trim().min(1).optional(),
		alt: z.string().trim().max(500, "Alt text should not exceed 500 characters").optional(),
		width: assetWidthSchema,
	}),
	z.object({
		type: z.literal("answerLines"),
		lines: z.number().int().min(1, "At least one answer line is required").max(60, "Too many answer lines"),
	}),
]);

// Authors embed maths as LaTeX inside the plain text blocks using `$...$`
// (inline) and `$$...$$` (display) delimiters. Reject a stray or unbalanced
// delimiter so a half-typed formula can't be saved.
const validateMathDelimiters = (blocks: z.infer<typeof contentBlockSchema>[], ctx: z.RefinementCtx) => {
	blocks.forEach((block, index) => {
		if (block.type !== "text") {
			return;
		}
		for (const language of ["en", "cy"] as const) {
			const value = block[language];
			if (value && !hasBalancedMathDelimiters(value)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [index, language],
					message: "Unbalanced maths delimiter. Wrap formulae as $...$ or $$...$$, and escape a literal dollar as \\$.",
				});
			}
		}
	});
};

const contentBlocksSchema = z.array(contentBlockSchema).superRefine(validateMathDelimiters).optional();

// English localised content: examiner comments + mark scheme image. The
// question body is authored as ordered `contentBlocks`; presence of English
// body text is enforced (for leaf questions) via `requireEnglishContent`.
const buildEnglishContentSchema = () =>
	z.object({
		examinersComments: z.string().trim().max(2000, "Examiner comments should not exceed 2000 characters").optional(),
		...localisedImageFields,
	});

// Welsh localised content: examiner comments + mark scheme image. The Welsh
// body text is enforced for the WJEC brand via `requireWelshForWjec`.
const welshContentSchema = z
	.object({
		examinersComments: z.string().trim().max(2000, "Examiner comments should not exceed 2000 characters").optional(),
		...localisedImageFields,
	})
	.optional();

const buildQuestionObjectSchema = () =>
	z.object({
		brand: z.enum(["wjec", "eduqas"]),
		/** Reference to the owning Paper; paper-level facts are inherited from it (QB2-223). */
		paperId: z.string().trim().min(1, "Paper reference is required"),
		paperCode: z.string().trim().min(1, "Paper code is required"),
		paperCodeAS400: z.string().trim().min(1).max(100).optional(),
		language: z.enum(["en", "cy"]),
		questionNumber: z
			.string({
				required_error: "Question number is required",
				invalid_type_error: "Question number must be a string",
			})
			.trim()
			.min(1, "Question number is required")
			.max(30, "Question number is too long"),
		maxMarks: z
			.number({ required_error: "Max marks is required", invalid_type_error: "Max marks must be a number" })
			.int()
			.positive("Max marks must be greater than 0"),
		sourcePage: z.number().int().positive().optional(),
		markSchemePage: z.number().int().positive().optional(),
		markSchemeContent: z
			.object({
				prompt: z.string().nullable().optional(),
				marks: z.number().nullable().optional(),
				ao: z.record(z.string(), z.number()).optional(),
				markCodes: z.array(z.string()).optional(),
				comments: z.array(z.string()).optional(),
				blocks: z
					.array(
						z.union([
							z.object({ type: z.literal("text"), en: z.string(), cy: z.string().optional() }),
							z.object({ type: z.literal("table"), columns: z.number(), rows: z.array(z.array(z.string())) }),
							z.object({
								type: z.literal("asset"),
								en: z.string().optional(),
								cy: z.string().optional(),
								enRaster: z.string().optional(),
								cyRaster: z.string().optional(),
							}),
							z.object({ type: z.literal("asset"), en: z.string().optional(), cy: z.string().optional() }),
						]),
					)
					.optional(),
			})
			.optional(),
		answerLines: z.number().int().min(0, "Answer lines cannot be negative").optional(),
		en: buildEnglishContentSchema(),
		cy: welshContentSchema,
		contentBlocks: contentBlocksSchema,
		assessmentObjectives: assessmentObjectivesSchema,
		specification: specificationSchema,
		coreMappings: coreMappingsSchema,
		metadata: metadataSchema,
		status: z.enum(QUESTION_STATUSES as unknown as [string, ...string[]]).default("draft") as unknown as z.ZodDefault<
			z.ZodEnum<[QuestionStatus, ...QuestionStatus[]]>
		>,
		parentId: z.string().trim().min(1).nullable().optional(),
	});

const questionObjectSchema = buildQuestionObjectSchema();

// Leaf questions must carry English question content — authored either as
// ordered blocks or via the legacy field. Grouped roots hold their content in
// their parts and so skip this refinement.
const requireEnglishContent = (data: { contentBlocks?: unknown; en?: unknown; cy?: unknown }, ctx: z.RefinementCtx) => {
	if (!getQuestionContentText(data as Parameters<typeof getQuestionContentText>[0], "en")) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ["contentBlocks"],
			message: "Question content (English) is required",
		});
	}
};

const requireWelshForWjec = (
	data: { brand?: string; contentBlocks?: unknown; en?: unknown; cy?: unknown },
	ctx: z.RefinementCtx,
) => {
	if (data.brand !== "wjec") {
		return;
	}
	if (!getQuestionContentText(data as Parameters<typeof getQuestionContentText>[0], "cy")) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ["contentBlocks"],
			message: "Question content (Welsh) is required for WJEC",
		});
	}
};

export const createQuestionSchema = questionObjectSchema
	.superRefine(requireEnglishContent)
	.superRefine(requireWelshForWjec);

/**
 * Authoring schema for a grouped root (a root question that has parts). Such a
 * root holds its content in its parts, so neither the English nor the Welsh
 * question content is required here.
 */
export const createGroupedRootQuestionSchema = questionObjectSchema;

const draftString = z.preprocess(blankToUndefined, z.string().trim().optional());

const draftLocalisedContentSchema = z.preprocess(
	blankToUndefined,
	z
		.object({
			examinersComments: z.preprocess(
				blankToUndefined,
				z.string().trim().max(2000, "Examiner comments should not exceed 2000 characters").optional(),
			),
			...localisedImageFields,
		})
		.optional(),
);

/**
 * Lenient schema for saving a question as a draft. Only the brand is required;
 * every other field is optional so an author can build a question up over time.
 * Publish-readiness is enforced separately via {@link getPublishBlockers} — a
 * draft can never be published until those rules are met.
 */
export const draftQuestionSchema = z.object({
	brand: z.enum(["wjec", "eduqas"]),
	/** Reference to the owning Paper; paper-level facts are inherited from it (QB2-223). */
	paperId: draftString,
	paperCode: draftString,
	paperCodeAS400: z.preprocess(blankToUndefined, z.string().trim().max(100).optional()),
	language: z.enum(["en", "cy"]).optional().default("en"),
	questionNumber: z.preprocess(blankToUndefined, z.string().trim().max(30, "Question number is too long").optional()),
	maxMarks: z.preprocess(blankToUndefined, z.number().int().positive("Max marks must be greater than 0").optional()),
	sourcePage: z.preprocess(blankToUndefined, z.number().int().positive().optional()),
	markSchemePage: z.preprocess(blankToUndefined, z.number().int().positive().optional()),
	markSchemeContent: z
		.object({
			prompt: z.string().nullable().optional(),
			marks: z.number().nullable().optional(),
			ao: z.record(z.string(), z.number()).optional(),
			markCodes: z.array(z.string()).optional(),
			comments: z.array(z.string()).optional(),
			blocks: z
				.array(
					z.union([
						z.object({ type: z.literal("text"), en: z.string(), cy: z.string().optional() }),
						z.object({ type: z.literal("table"), columns: z.number(), rows: z.array(z.array(z.string())) }),
						z.object({
							type: z.literal("asset"),
							en: z.string().optional(),
							cy: z.string().optional(),
							enRaster: z.string().optional(),
							cyRaster: z.string().optional(),
						}),
						z.object({ type: z.literal("asset"), en: z.string().optional(), cy: z.string().optional() }),
					]),
				)
				.optional(),
		})
		.optional(),
	answerLines: z.preprocess(blankToUndefined, z.number().int().min(0, "Answer lines cannot be negative").optional()),
	en: draftLocalisedContentSchema,
	cy: draftLocalisedContentSchema,
	contentBlocks: contentBlocksSchema,
	assessmentObjectives: assessmentObjectivesSchema,
	specification: specificationSchema,
	coreMappings: coreMappingsSchema,
	metadata: metadataSchema,
	status: z.enum(QUESTION_STATUSES as unknown as [string, ...string[]]).default("draft") as unknown as z.ZodDefault<
		z.ZodEnum<[QuestionStatus, ...QuestionStatus[]]>
	>,
	parentId: z.string().trim().min(1).nullable().optional(),
});

export const updateQuestionSchema = draftQuestionSchema
	.partial()
	.extend({
		rejectionReason: z.string().trim().max(REJECTION_REASON_MAX_LENGTH, "Rejection reason is too long").optional(),
	})
	.refine((data) => Object.keys(data).length > 0, {
		message: "At least one field must be provided for update",
	});

/**
 * Stored review provenance. Server-written only. It is not part of
 * {@link createQuestionSchema} or {@link updateQuestionSchema}, so a client
 * body cannot set it. Zod strips the key on those schemas.
 */
const reviewGateSchema = z.object({
	userId: z.string().trim().min(1),
	email: z.string().trim().min(1).optional(),
	at: z.string().datetime(),
});

export const reviewProvenanceSchema = z.object({
	extracted: reviewGateSchema
		.extend({
			jobId: z.string().trim().min(1),
			executionArn: z.string().trim().min(1).optional(),
		})
		.optional(),
	staged: reviewGateSchema.optional(),
	published: reviewGateSchema.optional(),
});

export type CreateQuestionInput = z.infer<typeof createQuestionSchema>;
export type DraftQuestionInput = z.infer<typeof draftQuestionSchema>;
export type UpdateQuestionInput = z.infer<typeof updateQuestionSchema>;

export const validateQuestion = <T>(
	schema: z.ZodSchema<T>,
	data: unknown,
): { success: true; data: T } | { success: false; errors: Record<string, string> } => {
	const result = schema.safeParse(data);

	if (!result.success) {
		const fieldErrors = result.error?.flatten().fieldErrors;
		const errors: Record<string, string> = {};

		for (const [key, errorMessages] of Object.entries(fieldErrors)) {
			if (Array.isArray(errorMessages) && errorMessages.length > 0) {
				errors[key] = errorMessages[0];
			}
		}
		return { success: false, errors };
	}

	return { success: true, data: result.data };
};

export const uploadAssetSchema = z.object({
	filename: z.string().min(1, "Filename is required"),
	contentType: z.enum([
		"image/png",
		"image/jpg",
		"image/jpeg",
		"image/gif",
		"image/webp",
		"image/svg+xml",
		"application/pdf",
	]),
});

export type UploadAssetSchema = z.infer<typeof uploadAssetSchema>;

// ─── Paper schemas ──────────────────────────────────────────────────

const paperAttachmentSchema = z.object({
	id: z.string().trim().min(1, "Attachment id is required"),
	label: z.string().trim().max(200, "Label should not exceed 200 characters").optional(),
	en: z.string().trim().min(1, "An English PDF is required"),
	cy: z.string().trim().min(1).optional(),
});

/**
 * Body schema for creating or updating a paper. `brand` is resolved from the
 * API Gateway stage variable (as with questions), so it is not part of the
 * request body. `id` is derived server-side from `(brand, paperCode, series)`.
 *
 * The Paper is the authoritative home of the paper-level facts a question
 * inherits (QB2-223): `subject` and `qualification` are required; `date` is the
 * single temporal fact; `tier` is optional (some papers have no tier). The
 * qualification is a Core Taxonomy reference (code + optional uuid/title), not
 * the old GCSE/AS/A-Level enum.
 */
export const upsertPaperSchema = z.object({
	paperCode: z.string().trim().min(1, "Paper code is required"),
	series: seriesSchema,
	date: z.coerce.date({ required_error: "Paper date is required", invalid_type_error: "Date must be a valid date" }),
	subject: z.union([z.string().trim().min(1, "Subject is required"), z.array(z.string().trim().min(1)).min(1)]),
	qualification: coreRefSchema,
	tier: coreRefSchema.optional(),
	attachments: z.array(paperAttachmentSchema).default([]),
});

export type UpsertPaperInput = z.infer<typeof upsertPaperSchema>;

// ─── Import job schemas ──────────────────────────────────────────────────
/**
 * The paper fields every import body carries, CSV or PDF. Defined once and
 * spread into both schemas so the two cannot drift.
 */
const importPaperShape = {
	/** The Paper the import belongs to. A CSV import stamps it onto each row. */
	paperId: z.string().trim().min(1, "Paper reference is required"),
	/** The paper's code. A CSV import stamps it onto each row, and the job shows it. */
	paperCode: z.string().trim().min(1, "Paper code is required"),
	/** Display hint for the job history. */
	subject: z.string().trim().min(1).optional(),
};

/**
 * Body schema for a CSV import. Rows are validated with the same lenient draft
 * schema the create-question endpoint uses so a file that previews cleanly
 * in the browser cannot then be rejected on upload. Any invalid row fails the
 * whole body, an import writes nothing until every row is good.
 */
const createCsvImportSchema = z.object({
	source: z.literal("csv"),
	fileName: z.string().trim().min(1).optional(),
	...importPaperShape,
	rows: z.array(draftQuestionSchema).min(1, "At least one row is required"),
});

/**
 * A blank Welsh key is absent. The both-or-neither check then sees no key.
 */
const optionalWelshPdfKey = z.preprocess((value: unknown) => {
	if (typeof value !== "string") {
		return value;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}, z.string().min(1).optional());

/**
 * Body schema for a PDF import. The keys are S3 asset keys from upload-asset.
 */
const createPdfImportSchema = z.object({
	source: z.literal("pdf"),
	sourceKeys: z
		.object({
			paper: z.string().trim().min(1, "Paper PDF is required"),
			markScheme: z.string().trim().min(1, "Mark scheme PDF is required"),
			specification: z.string().trim().min(1, "Specification PDF is required"),
			paperCy: optionalWelshPdfKey,
			markSchemeCy: optionalWelshPdfKey,
		})
		.transform(({ paperCy, markSchemeCy, ...keys }) => ({
			...keys,
			...(paperCy ? { paperCy } : {}),
			...(markSchemeCy ? { markSchemeCy } : {}),
		})),
	...importPaperShape,
});

/**
 * Body schema for the create-import endpoint. `source` selects the CSV or PDF shape.
 * Welsh PDF keys are both present or both absent.
 */
export const createImportSchema = z
	.discriminatedUnion("source", [createCsvImportSchema, createPdfImportSchema])
	.superRefine((data, ctx) => {
		if (data.source !== "pdf") {
			return;
		}
		const hasPaper = Boolean(data.sourceKeys.paperCy);
		const hasMarkScheme = Boolean(data.sourceKeys.markSchemeCy);
		if (hasPaper === hasMarkScheme) {
			return;
		}
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ["sourceKeys", hasPaper ? "markSchemeCy" : "paperCy"],
			message: "Welsh paper and Welsh mark scheme are both required",
		});
	});
export type CreateImportInput = z.infer<typeof createImportSchema>;

/**
 * Parse and validate JSON body
 */
export const parseJsonBody = <T>(
	body: string | null,
	schema: z.ZodType<T, z.ZodTypeDef, unknown>,
): { success: true; data: T } | { success: false; errors: Array<{ field: string; message: string }> } => {
	if (!body) {
		return {
			success: false,
			errors: [{ field: "body", message: "Request body is required" }],
		};
	}

	try {
		const parsed = JSON.parse(body);
		const result = schema.safeParse(parsed);

		if (!result.success) {
			return {
				success: false,
				errors: formatZodErrors(result.error),
			};
		}

		return { success: true, data: result.data };
	} catch {
		return {
			success: false,
			errors: [{ field: "body", message: "Invalid JSON in request body" }],
		};
	}
};

/**
 * Validate UUID format
 */
export const isValidUuid = (id: string): boolean => {
	const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
	return uuidRegex.test(id);
};

/**
 * Format Zod errors
 */
export const formatZodErrors = (error: z.ZodError): Array<{ field: string; message: string }> => {
	return error.errors.map((err) => ({
		field: err.path.join("."),
		message: err.message,
	}));
};

export interface ValidationResult {
	valid: boolean;
	errors: Array<{ field: string; message: string }>;
}

/**
 * Validate question data against brand language support
 */
export const validateQuestionForBrand = (brand: Brand, data: { language?: string }): ValidationResult => {
	if (data.language && !validateLanguage(brand, data.language)) {
		return {
			valid: false,
			errors: [
				{
					field: "language",
					message: `Language '${data.language}' is not supported for ${BRANDS[brand].name} brand. Supported languages: ${BRANDS[brand].supportedLanguages.join(", ")}`,
				},
			],
		};
	}

	return { valid: true, errors: [] };
};
